import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { POPULAR_MAKES } from '../mysearch/mysearchConstants';

interface SelectOption {
  value: string;
  label: string;
  image?: string;
}

// Default image for "Any" option
const DEFAULT_ANY_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23a0a0b0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='7' height='7'/%3E%3Crect x='14' y='3' width='7' height='7'/%3E%3Crect x='14' y='14' width='7' height='7'/%3E%3Crect x='3' y='14' width='7' height='7'/%3E%3C/svg%3E";

// Fallback car icon for makes without a logo
export const DEFAULT_CAR_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23a0a0b0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 17H3a2 2 0 0 1-2-2v-4l3.5-6h13L21 11v4a2 2 0 0 1-2 2h-2'/%3E%3Ccircle cx='7.5' cy='17.5' r='2.5'/%3E%3Ccircle cx='16.5' cy='17.5' r='2.5'/%3E%3C/svg%3E";

interface SelectWithImagesProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  options: SelectOption[];
  className?: string;
  size?: 'default' | 'small';
  showSearch?: boolean;
}

const OptionRow: React.FC<{ option: SelectOption; selected: boolean; onSelect: (v: string) => void }> = ({ option, selected, onSelect }) => (
  <div
    className="px-3 py-2"
    style={{
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: selected ? 'var(--primary-color)' : 'transparent',
    }}
    onClick={() => onSelect(option.value)}
    onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
    onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <img
      src={option.image || DEFAULT_CAR_IMAGE}
      alt={option.label}
      style={{ width: '20px', height: '20px', objectFit: 'contain', flexShrink: 0, opacity: option.image ? 1 : 0.5 }}
      onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.5'; }}
    />
    <span style={{ flex: 1, color: 'white' }}>{option.label}</span>
  </div>
);

export const SelectWithImages: React.FC<SelectWithImagesProps> = ({
  value,
  onChange,
  placeholder,
  options,
  className = '',
  size = 'default',
  showSearch = false,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('search.any');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions =
    showSearch && searchTerm
      ? options.filter(opt =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;

  const popularOptions = !searchTerm
    ? options.filter(opt =>
        POPULAR_MAKES.some(pm => pm.toLowerCase() === opt.label.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const sizeClass = size === 'small' ? 'form-select-sm' : '';
  // Use filter-select class to inherit CarSearchFilter styles
  const baseClasses = `form-select filter-select ${sizeClass} ${className}`.trim();

  // Adjust padding for small size
  const paddingRight = size === 'small' ? '0.75rem' : '0.75rem';

  return (
    <div
      ref={selectRef}
      className="position-relative"
      style={{ width: '100%' }}
    >
      <div
        className={baseClasses}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          userSelect: 'none',
          backgroundImage: 'none', // Remove Bootstrap's default chevron
          paddingRight: paddingRight, // Adjust padding since we're adding our own chevron
        }}
      >
        {value && selectedOption ? (
          <>
            <img
              src={selectedOption.image || DEFAULT_CAR_IMAGE}
              alt={selectedOption.label}
              style={{ width: '20px', height: '20px', objectFit: 'contain', flexShrink: 0, opacity: selectedOption.image ? 1 : 0.5 }}
              onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.5'; }}
            />
            <span style={{ flex: 1, textAlign: 'left', color: 'white' }}>
              {selectedOption.label}
            </span>
          </>
        ) : (
          <>
            <img
              src={DEFAULT_ANY_IMAGE}
              alt={resolvedPlaceholder}
              style={{
                width: '20px',
                height: '20px',
                objectFit: 'contain',
                flexShrink: 0,
                opacity: 0.8,
              }}
            />
            <span style={{ flex: 1, textAlign: 'left', color: 'rgba(255,255,255,0.6)' }}>
              {resolvedPlaceholder}
            </span>
          </>
        )}
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            flexShrink: 0,
          }}
        >
          <path
            d="M2 5l6 6 6-6"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          className="position-absolute w-100 rounded"
          style={{
            top: '100%',
            left: 0,
            marginTop: '4px',
            zIndex: 1050,
            maxHeight: '300px',
            overflowY: 'auto',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: '#1a1a20', // Dark background for dropdown
          }}
        >
          {showSearch && (
            <div className="p-2 border-bottom" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <input
                type="text"
                className="form-control form-control-sm filter-input"
                placeholder={t('select_with_images.search_placeholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{ color: 'white', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
          )}
          <div>
            {/* Any option */}
            <div
              className="px-3 py-2"
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: value === '' ? 'rgba(255,255,255,0.1)' : 'transparent',
              }}
              onClick={() => handleSelect('')}
              onMouseEnter={e => {
                if (value !== '')
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
              }}
              onMouseLeave={e => {
                if (value !== '')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <img
                src={DEFAULT_ANY_IMAGE}
                alt={resolvedPlaceholder}
                style={{ width: '16px', height: '16px', objectFit: 'contain', flexShrink: 0, opacity: 0.8 }}
              />
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>{resolvedPlaceholder}</span>
            </div>

            {/* Popular Makes section */}
            {popularOptions.length > 0 && (
              <>
                <div
                  className="px-3"
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: 'rgba(255,255,255,0.35)',
                    textTransform: 'uppercase',
                    paddingTop: '10px',
                    paddingBottom: '4px',
                  }}
                >
                  Popular Makes
                </div>
                {popularOptions.map(option => (
                  <OptionRow key={`pop-${option.value}`} option={option} selected={value === option.value} onSelect={handleSelect} />
                ))}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '6px 0' }} />
                <div
                  className="px-3"
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: 'rgba(255,255,255,0.35)',
                    textTransform: 'uppercase',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                  }}
                >
                  All Makes
                </div>
              </>
            )}

            {/* Full list */}
            {filteredOptions.map(option => (
              <OptionRow key={option.value} option={option} selected={value === option.value} onSelect={handleSelect} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
